import { IconBuildings } from 'central-icons/IconBuildings';
import { IconLayersThree } from 'central-icons/IconLayersThree';
import { IconMapPin } from 'central-icons/IconMapPin';
import { useEffect, useState } from 'react';

import { CircleGlobeIcon } from '@revelio/assets';
import { LinkedinIcon } from '@revelio/core';
import { Text } from '@revelio/ui';

import { useCompanyCompetitors } from '../../hooks/useCompanyCompetitors';
import { AddCompanyControl } from './AddCompanyControl';
import styles from './EntityInfoContent.module.css';
import type { EntityInfoData } from './EntityInfoPopover';
import layoutStyles from './entity-info-content.module.css';
import { useEntityInfoOpenStore } from './entity-info-open-context';
import { trackEntityInfoInteraction } from './entity-info-tracking';

type EntityInfoContentProps = {
  nodeName: string;
  entityInfo: EntityInfoData;
  /** Whether the "+"/competitors add flow applies. Resolved by the popover, so
   * its skeleton can reserve the same rows this card renders. */
  showAddV2: boolean;
};

/** "View Competitors" → expands to the company's competitors as addable chips.
 * Flag- and context-gated by the caller. */
const CompetitorsSection = ({ rcid }: { rcid: number }) => {
  // Seed from the open-store so an expanded list survives the tree's row
  // remounts (adding a competitor toggles selection, which remounts the row).
  const openStore = useEntityInfoOpenStore();
  const [expandRequested, setExpandRequested] = useState(
    openStore?.expandedRcidRef.current === rcid
  );
  // On mount — i.e. when the tooltip opens — rather than on the click, so
  // expanding lands on a populated list. cache-first, so reopening is free.
  const { competitors, isLoading } = useCompanyCompetitors(rcid, {
    enabled: true,
  });
  const expand = () => {
    setExpandRequested(true);
    if (openStore) openStore.expandedRcidRef.current = rcid;
    // A click while the list is still loading has no count to report yet.
    trackEntityInfoInteraction('competitors_expanded', {
      ...(isLoading ? {} : { competitor_count: competitors.length }),
    });
  };

  // Resolved to nothing — don't offer a link that expands into an empty box.
  // Fetching on open means we know this before the reader can click it.
  if (!isLoading && competitors.length === 0) {
    return null;
  }

  // The click asks to expand; the list opens once there is something to open it
  // onto. Expanding into placeholders and then swapping them for chips grew the
  // tooltip past its final height and snapped it back — a worse flash than the
  // wait it covered. Until then the link simply stays put.
  const expanded = expandRequested && competitors.length > 0;

  if (!expanded) {
    return (
      <Text
        as="button"
        type="button"
        fontSize="x-sm"
        className={styles.viewCompetitors}
        onClick={(e) => {
          e.stopPropagation();
          expand();
        }}
        // Keep focus in the popover so the click doesn't blur-close the tooltip.
        onMouseDown={(e) => e.preventDefault()}
        data-testid="entity-info-view-competitors"
      >
        View Competitors
      </Text>
    );
  }

  return (
    <div className={styles.competitors} data-testid="entity-info-competitors">
      <div className={styles.competitorsTitle}>COMPETITORS</div>
      <div className={styles.competitorChips}>
        {competitors.map((competitor) => (
          <AddCompanyControl
            key={competitor.rcid}
            rcid={competitor.rcid}
            name={competitor.name}
            source="tooltip_competitor"
            testId="entity-info-competitor"
          />
        ))}
      </div>
    </div>
  );
};

export const EntityInfoContent = ({
  nodeName,
  entityInfo,
  showAddV2,
}: EntityInfoContentProps) => {
  // The region beside the city is the STATE where there is one, falling back to
  // the country — the design's "Menlo Park, California", and it puts a location
  // on the US companies whose `country` the mapper leaves empty. Any part can be
  // absent (often all of them), so the parts are filtered rather than joined
  // around a fixed comma, and an empty result drops the line entirely.
  const location = [entityInfo.city, entityInfo.state || entityInfo.country]
    .filter(Boolean)
    .join(', ');

  // A parent chip on offer is the denominator for "who adds the parent". The
  // tree remounts its rows — and this card — when a company is toggled, so the
  // open-store remembers which rcid already reported during this open.
  const openStore = useEntityInfoOpenStore();
  const canAddParent =
    showAddV2 &&
    Boolean(entityInfo.hasUltimateParent) &&
    entityInfo.ult_parent_rcid != null;
  const rcid = entityInfo.rcid;
  useEffect(() => {
    if (!canAddParent || rcid == null) return;
    if (openStore?.reportedParentRcidRef.current === rcid) return;
    if (openStore) openStore.reportedParentRcidRef.current = rcid;
    trackEntityInfoInteraction('parent_offered');
  }, [canAddParent, rcid, openStore]);

  return (
    <>
      <div className={layoutStyles.infoRow}>
        <Text fontSize="sm" weight="semibold" className={styles.title}>
          {nodeName}
        </Text>
        <div className={layoutStyles.spacer} />

        <div className={layoutStyles.links}>
          {entityInfo.primary_linkedin_url && (
            <a
              href={entityInfo.primary_linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              // Keep focus inside the filter popover so opening the link in a new
              // tab doesn't blur-close the surrounding dropdown/tooltip.
              onMouseDown={(e) => e.preventDefault()}
              data-testid="entity-info-linkedin"
            >
              <LinkedinIcon />
            </a>
          )}
          {entityInfo.primary_website && (
            <a
              href={'https://' + entityInfo.primary_website}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              // Keep focus inside the filter popover so opening the link in a new
              // tab doesn't blur-close the surrounding dropdown/tooltip.
              onMouseDown={(e) => e.preventDefault()}
              data-testid="entity-info-website"
            >
              <CircleGlobeIcon />
            </a>
          )}
        </div>
      </div>
      {entityInfo.hasUltimateParent &&
        (showAddV2 && entityInfo.ult_parent_rcid != null ? (
          // "Subsidiary of" stays a label; the parent itself is a chip, so every
          // company the tooltip offers to add looks the same.
          <div className={styles.subsidiary}>
            <Text as="span" fontSize="x-sm" className={styles.detail}>
              <IconLayersThree
                size="0.625rem"
                className={layoutStyles.detailIcon}
              />
              Subsidiary of
            </Text>
            <AddCompanyControl
              rcid={entityInfo.ult_parent_rcid}
              name={entityInfo.ult_parent_name ?? ''}
              source="tooltip_parent"
            />
          </div>
        ) : (
          <div className={layoutStyles.detailRow}>
            <Text as="span" fontSize="x-sm" className={styles.detail}>
              <IconLayersThree
                size="0.625rem"
                className={layoutStyles.detailIcon}
              />
              Subsidiary of {entityInfo.ult_parent_name}
            </Text>
          </div>
        ))}

      <div className={layoutStyles.detailRow}>
        <Text fontSize="x-sm" className={styles.detail}>
          {/* Not the `CompanyIcon` asset: it is a base64 image with a white
              stroke baked in, so it cannot take `currentColor` and is invisible
              on the light tooltip. */}
          <IconBuildings size="0.625rem" className={layoutStyles.detailIcon} />
          {entityInfo.industry}
        </Text>
      </div>

      {location && (
        <div className={layoutStyles.infoRow}>
          <Text fontSize="x-sm" className={styles.detail}>
            <IconMapPin size="0.625rem" className={layoutStyles.detailIcon} />
            {location}
          </Text>
        </div>
      )}

      {showAddV2 && entityInfo.rcid != null && (
        <CompetitorsSection rcid={entityInfo.rcid} />
      )}
    </>
  );
};
